import React, { useState } from 'react';

interface ApproveItemDialogProps {
  itemName: string;
  employeeOptions: { value: string; label: string }[];
  defaultEmployee?: string;
  onConfirm: (employeeName: string) => void;
  onCancel: () => void;
}

/** Popup shown when approving a requisition item: pick the employee who will
 *  handle the auto-created payment order. */
const ApproveItemDialog: React.FC<ApproveItemDialogProps> = ({
  itemName, employeeOptions, defaultEmployee = '', onConfirm, onCancel,
}) => {
  const [employee, setEmployee] = useState(defaultEmployee);
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    if (!employee) return;
    setSaving(true);
    await onConfirm(employee);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Approve Item</h3>
        <p className="text-sm text-gray-600">
          Approving <span className="font-medium">"{itemName}"</span> will create a payment order.
          Select the employee who will make this payment order:
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Employee <span className="text-red-500">*</span>
          </label>
          <select
            value={employee}
            onChange={(e) => setEmployee(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500"
          >
            <option value="">Select employee...</option>
            {employeeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="flex justify-end space-x-3 pt-2">
          <button type="button" onClick={onCancel} disabled={saving}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} disabled={!employee || saving}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
            {saving ? 'Approving...' : 'Approve & Create Payment Order'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApproveItemDialog;
